import React, { useCallback, useEffect, useState } from "react";
import { MdClose, MdAdd, MdDelete } from "react-icons/md";
import { toast } from "react-toastify";
import { useForm } from "react-hook-form";
import { adminApiService } from "services/api";

const ManualOrderForm = ({ onClose }) => {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  const [cities, setCities] = useState([]);
  const [areas, setAreas] = useState([]);
  const [deliveryLocation, setDeliveryLocation] = useState("");
  const [shippingCost, setShippingCost] = useState(0);
  const [categories, setCategories] = useState([]);
  const [productRows, setProductRows] = useState([
    {
      id: Date.now(),
      categoryId: "",
      productId: "",
      quantity: 1,
      products: [],
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);

  // Fetch initial data
  useEffect(() => {
    fetchCityList();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await adminApiService.getAllCategories();
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error("Error fetching categories:", error);
      toast.error("Failed to fetch categories");
    }
  };

  const fetchProductByCategory = async (categoryId) => {
    if (!categoryId) return [];

    try {
      const response = await adminApiService.getProductsByCategory(categoryId);

      if (!response.ok) throw new Error("Failed to fetch products.");
      const data = await response.json();
      return data.allProducts || [];
    } catch (error) {
      console.error("Fetch error:", error);
      toast.error("Error fetching products.");
      return [];
    }
  };

  const fetchCityList = async () => {
    try {
      const response = await adminApiService.getCityList();
      const data = await response.json();
      setCities(data.cities);
    } catch (error) {
      console.error("Error fetching city list:", error);
    }
  };

  const fetchAreaByDistrict = async (district) => {
    try {
      const response = await adminApiService.getAreaByDistrict(district);

      const data = await response.json();
      setAreas(data.areas.data || []);
    } catch (error) {
      console.error("Error fetching area by district:", error);
    }
  };

  // Handle category change for a specific product row
  const handleCategoryChange = async (rowId, categoryId) => {
    setProductRows((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          return {
            ...row,
            categoryId,
            productId: "", // Reset product selection
            products: [], // Clear products while loading
          };
        }
        return row;
      })
    );

    // Fetch products for this category
    if (categoryId) {
      const products = await fetchProductByCategory(categoryId);
      setProductRows((prev) =>
        prev.map((row) => {
          if (row.id === rowId) {
            return { ...row, products };
          }
          return row;
        })
      );
    }
  };

  // Handle product change for a specific row
  const handleProductChange = (rowId, field, value) => {
    setProductRows((prev) =>
      prev.map((row) => {
        if (row.id === rowId) {
          return { ...row, [field]: value };
        }
        return row;
      })
    );
  };

  // Add new product row
  const addProductRow = () => {
    setProductRows((prev) => [
      ...prev,
      {
        id: Date.now(),
        categoryId: "",
        productId: "",
        quantity: 1,
        products: [],
      },
    ]);
  };

  // Remove product row
  const removeProductRow = (rowId) => {
    setProductRows((prev) => prev.filter((row) => row.id !== rowId));
  };

  // Watch for city and area changes
  const selectedCity = watch("city");
  const selectedArea = watch("area");

  useEffect(() => {
    if (selectedCity) {
      fetchAreaByDistrict(selectedCity);
      setValue("area", "");
      setValue("thana", "");
      setValue("zip", "");

      if (selectedCity === "Dhaka" || selectedCity === "Dhaka (Sub-Area)") {
        setDeliveryLocation("Dhaka");
        setShippingCost(80);
      } else {
        setDeliveryLocation("Outside Dhaka");
        setShippingCost(120);
      }
    }
  }, [selectedCity, setValue]);

  useEffect(() => {
    if (selectedArea) {
      const areaObj = areas.find((a) => a.name === selectedArea);
      if (areaObj) {
        setValue("thana", areaObj.thana);
        setValue("zip", areaObj.post_code);
      }
    }
  }, [selectedArea, areas, setValue]);

  const onSubmit = async (data) => {
    // Validate that all product rows have both category and product selected
    const validProducts = productRows.filter(
      (row) => row.categoryId && row.productId
    );

    if (validProducts.length === 0) {
      toast.error("Please select at least one product");
      return;
    }

    setIsLoading(true);
    try {
      const orderData = {
        customerName: data.customerName,
        customerEmail: data.customerEmail,
        customerPhone: data.customerPhone,
        shippingAddress: data.shippingAddress,
        city: data.city,
        area: data.area,
        thana: data.thana,
        zip: data.zip,
        cartItems: validProducts.map((row) => ({
          id: row.productId,
          quantity: row.quantity,
        })),
        discount: 0,
        shippingCost,
        deliveryLocation,
      };

      const response = await adminApiService.initManualOrder(orderData);

      if (!response.ok) {
        throw new Error("Failed to create order");
      }

      toast.success("Order created successfully!");
      onClose();
    } catch (error) {
      console.error("Error creating order:", error);
      toast.error("Failed to create order");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-black fixed inset-0 z-50 flex items-center justify-center bg-opacity-50 p-4">
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-xl bg-white shadow-2xl dark:bg-navy-800">
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-navy-800">
          <div className="flex items-center justify-between">
            <h3 className="text-2xl font-bold text-navy-700 dark:text-white">
              Create Manual Order
            </h3>
            <button
              onClick={onClose}
              className="rounded-full p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-navy-700"
            >
              <MdClose className="h-6 w-6" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6">
          {/* Customer Information Section */}
          <div className="mb-8">
            <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
              Customer Information
            </h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Customer Name *
                </label>
                <input
                  type="text"
                  {...register("customerName", {
                    required: "Name is required",
                  })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
                />
                {errors.customerName && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.customerName.message}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Customer Email
                </label>
                <input
                  type="email"
                  {...register("customerEmail", {
                    pattern: {
                      value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                      message: "Invalid email address",
                    },
                  })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
                />
                {errors.customerEmail && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.customerEmail.message}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Customer Phone *
                </label>
                <input
                  type="tel"
                  {...register("customerPhone", {
                    required: "Phone is required",
                    pattern: {
                      value: /^[0-9]{11}$/,
                      message: "Phone number must be 11 digits",
                    },
                  })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
                />
                {errors.customerPhone && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.customerPhone.message}
                  </p>
                )}
              </div>

              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Shipping Address *
                </label>
                <textarea
                  {...register("shippingAddress", {
                    required: "Address is required",
                  })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
                  rows="3"
                />
                {errors.shippingAddress && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.shippingAddress.message}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Delivery Information Section */}
          <div className="mb-8">
            <h4 className="mb-4 text-lg font-semibold text-navy-700 dark:text-white">
              Delivery Information
            </h4>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                  City *
                </label>
                <select
                  {...register("city", { required: "City is required" })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2.5 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
                >
                  <option value="">Select City</option>
                  {cities?.map((city) => (
                    <option key={city.value} value={city.value}>
                      {city.name}
                    </option>
                  ))}
                </select>
                {errors.city && (
                  <p className="mt-1 text-sm text-red-500">
                    {errors.city.message}
                  </p>
                )}
              </div>

              {selectedCity && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Area *
                  </label>
                  <select
                    {...register("area", { required: "Area is required" })}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2.5 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
                  >
                    <option value="">Select Area</option>
                    {areas?.map((area) => (
                      <option key={area.id} value={area.name}>
                        {area.name}
                      </option>
                    ))}
                  </select>
                  {errors.area && (
                    <p className="mt-1 text-sm text-red-500">
                      {errors.area.message}
                    </p>
                  )}
                </div>
              )}

              {selectedArea && (
                <>
                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Thana
                    </label>
                    <input
                      type="text"
                      readOnly
                      {...register("thana")}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2.5 dark:border-gray-600 dark:bg-navy-600 dark:text-white"
                    />
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Zip Code
                    </label>
                    <input
                      type="text"
                      readOnly
                      {...register("zip")}
                      className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2.5 dark:border-gray-600 dark:bg-navy-600 dark:text-white"
                    />
                  </div>
                </>
              )}
            </div>

            {deliveryLocation && (
              <div className="mt-4 rounded-lg bg-blue-50 p-4 dark:bg-navy-700">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
                    Delivery Location: {deliveryLocation}
                  </span>
                  <span className="text-sm font-bold text-blue-900 dark:text-blue-200">
                    Shipping Cost: ৳{shippingCost}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Products Section */}
          <div className="mb-8">
            <div className="mb-4 flex items-center justify-between">
              <h4 className="text-lg font-semibold text-navy-700 dark:text-white">
                Products
              </h4>
              <button
                type="button"
                onClick={addProductRow}
                className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
              >
                <MdAdd className="h-4 w-4" />
                Add Product
              </button>
            </div>

            <div className="space-y-4">
              {productRows.map((row, index) => (
                <div
                  key={row.id}
                  className="rounded-lg border border-gray-200 p-4 dark:border-gray-700"
                >
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
                    {/* Category Selection */}
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Category *
                      </label>
                      <select
                        value={row.categoryId}
                        onChange={(e) =>
                          handleCategoryChange(row.id, e.target.value)
                        }
                        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
                        required
                      >
                        <option value="">Select Category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Product Selection */}
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Product *
                      </label>
                      <select
                        value={row.productId}
                        onChange={(e) =>
                          handleProductChange(
                            row.id,
                            "productId",
                            e.target.value
                          )
                        }
                        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
                        disabled={!row.categoryId}
                        required
                      >
                        <option value="">
                          {row.categoryId
                            ? "Select Product"
                            : "Select Category First"}
                        </option>
                        {row.products.map((product) => (
                          <option key={product.id} value={product.id}>
                            {product.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Quantity */}
                    <div>
                      <label className="mb-2 block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Quantity *
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={row.quantity}
                        onChange={(e) =>
                          handleProductChange(
                            row.id,
                            "quantity",
                            parseInt(e.target.value) || 1
                          )
                        }
                        className="w-full rounded-lg border border-gray-300 px-3 py-2.5 transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-200 dark:border-gray-600 dark:bg-navy-700 dark:text-white dark:focus:border-blue-400"
                        required
                      />
                    </div>

                    {/* Actions */}
                    <div className="flex items-end">
                      {productRows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeProductRow(row.id)}
                          className="rounded-lg bg-red-500 p-2.5 text-white transition-colors hover:bg-red-600"
                        >
                          <MdDelete className="h-5 w-5" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Product Preview */}
                  {row.productId && (
                    <div className="mt-3 rounded-lg bg-gray-50 p-3 dark:bg-navy-600">
                      <div className="flex items-center gap-3">
                        {(() => {
                          const selectedProduct = row.products.find(
                            (p) => p.id === row.productId
                          );
                          return selectedProduct ? (
                            <>
                              <img
                                src={`${process.env.REACT_APP_API_URL_Plain}/config/uploads/products/product_images/${selectedProduct.product_images[0]}`}
                                alt={selectedProduct.name}
                                className="h-12 w-12 rounded-lg object-cover"
                              />
                              <div>
                                <p className="font-medium text-gray-900 dark:text-white">
                                  {selectedProduct.name}
                                </p>
                                <p className="text-sm text-gray-600 dark:text-gray-300">
                                  Quantity: {row.quantity}
                                </p>
                              </div>
                            </>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Footer Actions */}
          <div className="flex justify-end gap-4 border-t border-gray-200 pt-6 dark:border-gray-700">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-6 py-2.5 text-gray-700 transition-colors hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-navy-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="rounded-lg bg-blue-600 px-6 py-2.5 text-white transition-colors hover:bg-blue-700 disabled:opacity-50"
            >
              {isLoading ? "Creating Order..." : "Create Order"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManualOrderForm;
